import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Paper,
  Rating,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { ShopBarber } from '../../types/shop';
import { getOpenStatus } from '../../utils/businessHoursUtils';
import { getMockRatingSummary } from '../../types/review';
import { darkCardBg } from './darkFormStyles';
import { BookableService } from '../../types/booking';
import SampleDataBadge from './SampleDataBadge';

const accent = '#dd8709';

interface ShopBarberProfileModalProps {
  open: boolean;
  onClose: () => void;
  barber: ShopBarber | null;
  /** All bookable shop services; filtered internally to this barber's own services. */
  services: BookableService[];
  /** Starts the booking flow for this barber, optionally preselecting one service. */
  onBook: (service?: BookableService) => void;
}

interface TabPanelProps {
  value: number;
  index: number;
  children: React.ReactNode;
}

const TabPanel: React.FC<TabPanelProps> = ({ value, index, children }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

/**
 * Read-only pop-up profile for an individual shop barber, reached from a
 * shop's service list or "Meet the Team" section. Lets a visitor review the
 * barber's details, services and (mock) reviews, then either close it or
 * jump straight into the booking flow for that barber.
 */
const ShopBarberProfileModal: React.FC<ShopBarberProfileModalProps> = ({
  open,
  onClose,
  barber,
  services,
  onBook,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [tab, setTab] = useState(0);

  const barberServices = useMemo(
    () => services.filter((s) => s.barberId === barber?.id),
    [services, barber?.id]
  );

  const openStatus = useMemo(
    () => getOpenStatus(barber?.businessHours, undefined, undefined),
    [barber]
  );

  const ratingSummary = useMemo(
    () => (barber?.id ? getMockRatingSummary(barber.id) : null),
    [barber?.id]
  );

  // Reset to the Profile tab each time a different barber's modal is opened.
  React.useEffect(() => {
    if (open) setTab(0);
  }, [open, barber?.id]);

  if (!barber) return null;

  const barberName = `${barber.firstName || ''}`.trim();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { bgcolor: darkCardBg, color: '#fff' } }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 3,
          pb: 2,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Avatar
          src={barber.profileImageSelfLink}
          sx={{ width: 72, height: 72, border: '2px solid #fff' }}
        >
          <PersonIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight="bold" noWrap>
            {barberName || 'Barber'}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              mt: 0.5,
            }}
          >
            {barber.phoneNumber && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PhoneIcon
                  fontSize="small"
                  sx={{ color: 'rgba(255,255,255,0.6)' }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(255,255,255,0.6)' }}
                >
                  {barber.phoneNumber}
                </Typography>
              </Box>
            )}
            {barber.businessHours && (
              <Chip
                size="small"
                label={openStatus.isOpen ? 'Open Now' : 'Closed'}
                sx={{
                  bgcolor: openStatus.isOpen ? '#2e7d32' : '#616161',
                  color: '#fff',
                  fontWeight: 'bold',
                }}
              />
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} aria-label="Close" sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Tabs
        value={tab}
        onChange={(_e, value) => setTab(value)}
        variant="fullWidth"
        textColor="inherit"
        sx={{
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          '& .MuiTab-root': { color: 'rgba(255,255,255,0.6)' },
          '& .Mui-selected': { color: `${accent} !important` },
          '& .MuiTabs-indicator': { bgcolor: accent },
        }}
      >
        <Tab label="Profile" />
        <Tab label="Services" />
        <Tab label="Reviews" />
      </Tabs>

      <DialogContent sx={{ minHeight: 260 }}>
        <TabPanel value={tab} index={0}>
          <Typography
            variant="body2"
            sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}
          >
            {barberServices.length} service
            {barberServices.length !== 1 ? 's' : ''} offered
          </Typography>
          {ratingSummary && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
                flexWrap: 'wrap',
              }}
            >
              <Rating
                value={ratingSummary.averageRating}
                precision={0.1}
                readOnly
              />
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.6)' }}
              >
                {ratingSummary.averageRating.toFixed(1)} (
                {ratingSummary.totalReviews} reviews)
              </Typography>
              <SampleDataBadge />
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {barberServices.length === 0 && (
              <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>
                No services have been added yet.
              </Typography>
            )}
            {barberServices.map((bookable, i) => (
              <Paper
                key={`${bookable.service.id}-${i}`}
                sx={{
                  p: 1.5,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Box>
                  <Typography
                    fontWeight="bold"
                    sx={{ color: 'rgba(255,255,255,0.9)' }}
                  >
                    {bookable.service.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <AttachMoneyIcon
                        fontSize="small"
                        sx={{ color: accent }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: 'rgba(255,255,255,0.7)' }}
                      >
                        £{bookable.service.price ?? 0}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <AccessTimeIcon fontSize="small" sx={{ color: accent }} />
                      <Typography
                        variant="body2"
                        sx={{ color: 'rgba(255,255,255,0.7)' }}
                      >
                        {bookable.service.duration ?? 0} min
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => onBook(bookable)}
                  sx={{ bgcolor: accent, '&:hover': { bgcolor: '#b86e08' } }}
                >
                  Select & Book
                </Button>
              </Paper>
            ))}
          </Box>
        </TabPanel>

        <TabPanel value={tab} index={2}>
          {ratingSummary && ratingSummary.reviews.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ mb: 0.5 }}>
                <SampleDataBadge />
              </Box>
              {ratingSummary.reviews.map((review) => (
                <Box key={review.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Rating value={review.rating} size="small" readOnly />
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.5)' }}
                    >
                      {review.authorName}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255,255,255,0.7)' }}
                  >
                    {review.comment}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>
              No reviews yet.
            </Typography>
          )}
        </TabPanel>
      </DialogContent>

      <DialogActions
        sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.08)' }}
      >
        <Button onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)' }}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={() => onBook()}
          sx={{ bgcolor: accent, '&:hover': { bgcolor: '#b86e08' } }}
        >
          Book Now
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShopBarberProfileModal;
